import { Link, useOutletContext } from "react-router-dom";
import { CheckCircle2, Clock, MapPin, Stethoscope, Shield, Calendar, Phone, Award } from "lucide-react";

function useSettings() {
  const ctx = useOutletContext();
  return ctx?.settings;
}

function Section({ overline, title, children, kicker, id }) {
  return (
    <section id={id} className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {overline && <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-3">{overline}</div>}
      {title && <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{title}</h2>}
      {kicker && <p className="text-lg text-slate-600 leading-relaxed mt-3 max-w-2xl">{kicker}</p>}
      <div className="mt-10">{children}</div>
    </section>
  );
}

export function Home() {
  const s = useSettings();
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F8FAF8] via-white to-emerald-50/40 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-800 border border-emerald-200/60 px-3 py-1.5 rounded-full text-xs font-semibold">
              <Shield size={12} /> Accepting new patients
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900 mt-5 font-display">
              {s?.tagline || "Compassionate, modern care — on your schedule."}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mt-5 max-w-xl">
              Book a consultation with <span className="font-semibold text-slate-900">{s?.doctor_name || "your doctor"}</span>
              {s?.doctor_title ? `, ${s.doctor_title}` : ""}. Real-time availability, confirmation by email, and a dedicated appointment manager.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/book" className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl px-5 py-3 shadow-sm active:scale-[0.98] transition" data-testid="hero-book-btn">
                <Calendar size={16} /> Book an appointment
              </Link>
              <Link to="/services" className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-medium rounded-xl px-5 py-3 transition">
                Services offered
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              <Stat label="Years practising" value={s?.years_experience ? `${s.years_experience}+` : "—"} />
              <Stat label="Specialities" value={s?.specialities?.length || 0} />
              <Stat label="Languages" value={s?.languages?.length || 0} />
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,76,129,0.3)] bg-slate-100">
              {s?.photo_url && (
                <img src={s.photo_url} alt={s?.doctor_name || "Doctor"} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl border border-slate-200 shadow-lg p-4 flex items-center gap-3 max-w-xs">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center"><Award size={18} /></div>
              <div>
                <div className="text-sm font-semibold text-slate-900">{s?.doctor_title || "Qualified physician"}</div>
                <div className="text-xs text-slate-500">{s?.qualifications?.[0] || "Verified credentials"}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications & specialities */}
      <Section overline="Credentials" title="Qualified. Experienced. Approachable." kicker="A doctor who partners with you on a long-term care plan, not just a prescription.">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-7">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Qualifications</div>
            <ul className="space-y-3">
              {(s?.qualifications || []).map((q, i) => (
                <li key={i} className="flex items-start gap-2.5 text-slate-700">
                  <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-7">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Specialities</div>
            <div className="flex flex-wrap gap-2">
              {(s?.specialities || []).map((sp, i) => (
                <span key={i} className="px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200/60 rounded-full text-sm font-medium">{sp}</span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Services */}
      <Section overline="Services" title="What you can book">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {(s?.services || []).map((svc, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-emerald-300 transition">
              <div className="w-10 h-10 rounded-xl bg-[#0F4C81]/10 text-[#0F4C81] grid place-items-center mb-3"><Stethoscope size={18} /></div>
              <div className="font-display font-semibold text-slate-900">{svc.name}</div>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{svc.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Fees & timings */}
      <Section overline="Transparent" title="Consultation fees &amp; timings">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-7">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Consultation fees</div>
            <div className="space-y-3">
              {(s?.consultation_fees || []).map((f, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-0 border-slate-100">
                  <div className="font-medium text-slate-800">{f.type}</div>
                  <div className="font-display font-bold text-[#0F4C81]">{f.currency === "INR" ? "₹" : f.currency} {f.amount}</div>
                </div>
              ))}
            </div>
            <Link to="/book" className="mt-6 inline-flex items-center gap-2 text-emerald-700 font-semibold hover:text-emerald-800">
              <Calendar size={16} /> Book now
            </Link>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-7">
            <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Clinic location</div>
            <div className="flex items-start gap-2 text-slate-700">
              <MapPin size={18} className="text-emerald-600 mt-0.5" />
              <div>
                <div className="font-semibold">{s?.clinic_name}</div>
                <div className="text-sm text-slate-600">{s?.clinic_address}</div>
                <div className="text-sm text-slate-600">{s?.clinic_city}</div>
              </div>
            </div>
            {s?.phone && (
              <div className="flex items-center gap-2 text-slate-700 mt-3">
                <Phone size={16} className="text-emerald-600" />
                <a className="text-sm hover:underline" href={`tel:${s.phone}`}>{s.phone}</a>
              </div>
            )}
            {s?.clinic_map_url && (
              <div className="mt-4 rounded-xl overflow-hidden border border-slate-200 aspect-[16/9]">
                <iframe title="map" src={s.clinic_map_url} className="w-full h-full" loading="lazy" />
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto rounded-3xl bg-[#0F172A] text-white p-10 md:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Ready when you are</div>
            <h3 className="text-2xl md:text-3xl font-display font-bold">Book your consultation in under a minute.</h3>
            <p className="text-slate-300 mt-2 max-w-xl">Guest booking — no account required. You will receive a reference number instantly.</p>
          </div>
          <Link to="/book" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold rounded-xl px-6 py-3" data-testid="cta-book-btn">
            <Clock size={16} /> Book an appointment
          </Link>
        </div>
      </section>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export function About() {
  const s = useSettings();
  return (
    <Section overline="About" title={`Meet ${s?.doctor_name || "your doctor"}`} kicker={s?.doctor_title}>
      <div className="grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200 bg-slate-100">
            {s?.photo_url && <img src={s.photo_url} alt="doctor" className="w-full h-full object-cover" />}
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="text-lg text-slate-700 leading-relaxed">{s?.bio}</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            <InfoCard title="Qualifications" items={s?.qualifications} />
            <InfoCard title="Specialities" items={s?.specialities} />
            <InfoCard title="Languages" items={s?.languages} />
            <InfoCard title="Experience" items={s?.years_experience ? [`${s.years_experience}+ years`] : []} />
          </div>
        </div>
      </div>
    </Section>
  );
}

function InfoCard({ title, items }) {
  if (!items || !items.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-slate-800 text-sm">{it}</li>
        ))}
      </ul>
    </div>
  );
}

export function Services() {
  const s = useSettings();
  return (
    <Section overline="Specialities &amp; Services" title="What we treat" kicker="Transparent fees. Clear communication. No surprises.">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {(s?.services || []).map((svc, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-md transition">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center mb-4"><Stethoscope size={18} /></div>
            <div className="font-display text-lg font-semibold text-slate-900">{svc.name}</div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{svc.description}</p>
          </div>
        ))}
      </div>
      <div className="mt-10">
        <Link to="/book" className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl px-5 py-3">
          Book an appointment
        </Link>
      </div>
    </Section>
  );
}

export function Consultation() {
  const s = useSettings();
  return (
    <Section overline="Consultation" title="How it works">
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { n: "01", t: "Choose a consultation", d: "Pick in-clinic or video depending on availability." },
          { n: "02", t: "Select a slot", d: "Real-time availability from the doctor's calendar." },
          { n: "03", t: "Confirm &amp; attend", d: "You receive a reference number and status updates." },
        ].map((x) => (
          <div key={x.n} className="bg-white border border-slate-200 rounded-2xl p-7">
            <div className="font-mono text-emerald-600 text-sm">{x.n}</div>
            <div className="font-display text-xl font-semibold text-slate-900 mt-2" dangerouslySetInnerHTML={{__html: x.t}} />
            <p className="text-slate-600 text-sm mt-2 leading-relaxed" dangerouslySetInnerHTML={{__html: x.d}} />
          </div>
        ))}
      </div>
      <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-7">
        <div className="font-display font-semibold text-slate-900 mb-3">Consultation fees</div>
        <div className="divide-y divide-slate-100">
          {(s?.consultation_fees || []).map((f, i) => (
            <div key={i} className="flex items-center justify-between py-3">
              <div className="text-slate-800 font-medium">{f.type}</div>
              <div className="font-display font-bold text-[#0F4C81]">{f.currency === "INR" ? "₹" : f.currency} {f.amount}</div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

export function Contact() {
  const s = useSettings();
  return (
    <Section overline="Contact" title="Get in touch">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-7 space-y-4">
          <Row icon={<MapPin size={18} />} label="Clinic">
            <div className="font-semibold">{s?.clinic_name}</div>
            <div className="text-sm text-slate-600">{s?.clinic_address}</div>
            <div className="text-sm text-slate-600">{s?.clinic_city}</div>
          </Row>
          {s?.phone && <Row icon={<Phone size={18} />} label="Phone"><a href={`tel:${s.phone}`} className="hover:underline">{s.phone}</a></Row>}
          {s?.email && <Row icon={<Stethoscope size={18} />} label="Email"><a href={`mailto:${s.email}`} className="hover:underline">{s.email}</a></Row>}
          <Link to="/book" className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl px-5 py-3 mt-3">Book an appointment</Link>
        </div>
        {s?.clinic_map_url && (
          <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-[4/3] min-h-[300px]">
            <iframe title="map" src={s.clinic_map_url} className="w-full h-full" loading="lazy" />
          </div>
        )}
      </div>
    </Section>
  );
}

function Row({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">{icon}</div>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-slate-800">{children}</div>
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <Section overline="Privacy" title="Privacy Policy">
      <div className="prose max-w-3xl text-slate-700 leading-relaxed">
        <p>We collect only the information required to confirm and manage your appointment: your name, mobile number, email (if provided), chosen slot and a short reason for the visit.</p>
        <p className="mt-4">This information is stored securely and used solely to coordinate your care with the clinic. We do not sell your data. Access is limited to the doctor and authorised clinic staff.</p>
        <p className="mt-4">You may request deletion of your records at any time by contacting the clinic.</p>
      </div>
    </Section>
  );
}

export function Terms() {
  return (
    <Section overline="Terms" title="Appointment &amp; Clinic Terms">
      <div className="prose max-w-3xl text-slate-700 leading-relaxed space-y-4">
        <p>Appointments are offered subject to confirmation by the clinic. You will receive a booking reference as proof of your request.</p>
        <p>Please arrive 10 minutes before your slot. If you need to cancel or reschedule, contact the clinic as early as possible — slots are limited.</p>
        <p>No-shows may be charged a nominal fee at the discretion of the clinic.</p>
        <p>Information shared during consultation remains confidential and is used strictly for the purpose of your care.</p>
      </div>
    </Section>
  );
}
